import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import Joi from 'joi';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../../layout/Icon.jsx';
import { toast } from '../../shared/useToast.js';
import { PortalFrame } from './components/PortalFrame.jsx';
import { usePlatformAuth } from './usePlatformAuth.js';
import { platformError } from './platformApi.js';

const schema = Joi.object({
  currentPassword: Joi.string().required().messages({
    'string.empty': 'كلمة المرور الحالية مطلوبة',
    'any.required': 'كلمة المرور الحالية مطلوبة'
  }),
  newPassword: Joi.string().min(8).required().messages({
    'string.empty': 'كلمة المرور الجديدة مطلوبة',
    'string.min': 'كلمة المرور الجديدة يجب أن تكون 8 أحرف على الأقل',
    'any.required': 'كلمة المرور الجديدة مطلوبة'
  }),
  confirmPassword: Joi.string().valid(Joi.ref('newPassword')).required().messages({
    'string.empty': 'تأكيد كلمة المرور مطلوب',
    'any.only': 'كلمتا المرور غير متطابقتين',
    'any.required': 'تأكيد كلمة المرور مطلوب'
  })
});

export function PlatformChangePassword() {
  const navigate = useNavigate();
  const user = usePlatformAuth((s) => s.user);
  const changePassword = usePlatformAuth((s) => s.changePassword);
  const forced = !!user?.mustChangePassword;

  const [serverError, setServerError] = useState('');
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: joiResolver(schema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' }
  });

  const onSubmit = async (values) => {
    setServerError('');
    setLoading(true);
    try {
      await changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword
      });
      toast.success('تم تغيير كلمة المرور — سجّل الدخول بكلمة المرور الجديدة');
      navigate('/platform/login', { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) setServerError('كلمة المرور الحالية غير صحيحة');
      else setServerError(platformError(err, 'تعذّر تغيير كلمة المرور'));
    } finally {
      setLoading(false);
    }
  };

  const fields = [
    { name: 'currentPassword', label: 'كلمة المرور الحالية', autoComplete: 'current-password', autoFocus: true },
    { name: 'newPassword', label: 'كلمة المرور الجديدة (8 أحرف على الأقل)', autoComplete: 'new-password' },
    { name: 'confirmPassword', label: 'تأكيد كلمة المرور الجديدة', autoComplete: 'new-password' }
  ];

  return (
    <PortalFrame
      title="تغيير كلمة المرور"
      sub={user ? (user.fullName || user.username) : undefined}
      width={440}
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {forced ? (
          <div
            className="px-3 py-2.5 rounded-[10px] text-[12.5px] font-semibold mb-3"
            style={{ background: '#FFF7ED', color: '#9A3412' }}
          >
            كلمة المرور الحالية ابتدائية (bootstrap) ويجب تغييرها قبل استخدام اللوحة.
            اختر كلمة مرور قويّة لا تقل عن 8 أحرف.
          </div>
        ) : null}

        <div className="space-y-3">
          {fields.map((f) => (
            <div key={f.name}>
              <label className="block text-[12px] font-semibold text-ink-2 mb-1.5">{f.label}</label>
              <input
                type="password"
                dir="ltr"
                autoComplete={f.autoComplete}
                autoFocus={f.autoFocus}
                className={clsx('input', errors[f.name] && 'border-red')}
                style={{ textAlign: 'left' }}
                placeholder="••••••••"
                {...register(f.name)}
              />
              {errors[f.name] ? (
                <div className="text-red text-[11.5px] mt-1 font-semibold">{errors[f.name].message}</div>
              ) : null}
            </div>
          ))}
        </div>

        {serverError ? (
          <div className="mt-3 px-3 py-2.5 rounded-[10px] bg-[#FEE2E2] border border-[#FCA5A5] text-[#B91C1C] text-[12.5px] font-semibold flex items-center gap-2">
            <Icon name="x" size={14} />
            {serverError}
          </div>
        ) : null}

        <div className="text-[11.5px] text-ink-3 mt-3">
          بعد التغيير ستُنهى الجلسة الحالية وتُعاد إلى شاشة الدخول.
        </div>

        <button
          type="submit"
          disabled={loading}
          className={clsx('btn btn-primary btn-xl w-full justify-center mt-3', loading && 'opacity-70 cursor-not-allowed')}
        >
          <Icon name="check" size={16} />
          {loading ? 'جاري الحفظ...' : 'تغيير كلمة المرور'}
        </button>

        {!forced ? (
          <button
            type="button"
            className="btn w-full justify-center mt-2"
            onClick={() => navigate('/platform', { replace: true })}
          >
            رجوع إلى اللوحة
          </button>
        ) : null}
      </form>
    </PortalFrame>
  );
}
